import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, User, Award, TrendingUp, Eye } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { getWebinarThumbnail } from '../utils/thumbnailHelper';

const TrendingWebinarCard = ({ webinar }) => {
  const thumbnail = getWebinarThumbnail(webinar);
  
  return (
    <Link to={`/webinar/${webinar.webinar_id}`}>
      <Card className="overflow-hidden h-full transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer group">
        <div className="relative overflow-hidden aspect-video">
          <img
            src={thumbnail}
            alt={webinar.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=450&fit=crop';
            }}
          />
          
          {/* Trending or Most Watched Badge - Top Left */}
          {webinar.is_trending && (
            <div className="absolute top-3 left-3">
              <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600 font-semibold px-3 py-1">
                <TrendingUp className="h-3.5 w-3.5 mr-1 inline" />
                Trending Now
              </Badge>
            </div>
          )}
          
          {webinar.is_most_watched && !webinar.is_trending && (
            <div className="absolute top-3 left-3">
              <Badge className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:from-blue-600 hover:to-indigo-600 font-semibold px-3 py-1">
                <Eye className="h-3.5 w-3.5 mr-1 inline" />
                Most Watched
              </Badge>
            </div>
          )}
          
          {/* Category Badge - Top Right */}
          <div className="absolute top-3 right-3">
            <Badge className="bg-white/90 text-gray-900 hover:bg-white">
              {webinar.category}
            </Badge>
          </div>
          
          {/* View Count - Bottom Right - Only for trending/most watched */}
          {(webinar.is_trending || webinar.is_most_watched) && webinar.view_count > 0 && (
            <div className="absolute bottom-3 right-3">
              <Badge variant="secondary" className="bg-black/60 text-white hover:bg-black/70 backdrop-blur-sm">
                <Eye className="h-3 w-3 mr-1 inline" />
                {webinar.view_count.toLocaleString()} views
              </Badge>
            </div>
          )}
        </div>
        
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <Award className="h-4 w-4 text-blue-600" />
            <span className="text-xs font-medium text-blue-600">{webinar.level}</span>
          </div>
          
          <h3 className="font-semibold text-lg mb-2 line-clamp-2 text-gray-900 group-hover:text-blue-600 transition-colors">
            {webinar.title}
          </h3>
          
          <p className="text-sm text-gray-600 mb-4 line-clamp-2">
            {webinar.short_description || webinar.description?.substring(0, 120) + '...' || 'Explore this webinar to learn more'}
          </p>
          
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <User className="h-3.5 w-3.5" />
              <span>{webinar.speaker.name}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              <span>{webinar.duration}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default TrendingWebinarCard;
